import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import {
  useCreatePlanningPolicy,
  useUpdatePlanningPolicy,
  type PlanningPolicy,
} from "@/api/planningPolicy";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatRelativeTime } from "@/utils/formatters";

// Fields a regime doesn't use keep valid defaults and are dropped on submit.
const schema = z.object({
  name: z.string().trim().min(1),
  regime: z.enum(["service_level", "min_max"]),
  serviceLevel: z.number().min(0.5).max(0.999),
  lotSizingMethod: z.enum(["lead_time_coverage", "fixed_period"]),
  orderCoverageDays: z.number().int().min(1).max(365),
});

/** `policy` null creates a new policy. */
export function PolicyDialog({
  policy,
  onClose,
}: {
  policy: PlanningPolicy | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreatePlanningPolicy();
  const update = useUpdatePlanningPolicy();
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: policy?.name ?? "",
      regime: policy?.regime ?? "service_level",
      serviceLevel: policy?.serviceLevel ?? 0.95,
      lotSizingMethod: policy?.lotSizingMethod ?? "lead_time_coverage",
      orderCoverageDays: policy?.orderCoverageDays ?? 14,
    },
  });
  const regime = form.watch("regime");
  const lotSizingMethod = form.watch("lotSizingMethod");

  const submit = form.handleSubmit((values) => {
    const serviceLevel = values.regime === "service_level";
    const body = {
      name: values.name,
      regime: values.regime,
      serviceLevel: serviceLevel ? values.serviceLevel : null,
      lotSizingMethod: serviceLevel ? values.lotSizingMethod : null,
      orderCoverageDays:
        serviceLevel && values.lotSizingMethod === "fixed_period"
          ? values.orderCoverageDays
          : null,
    };
    if (policy)
      update.mutate({ id: policy.id, ...body }, { onSuccess: onClose });
    else create.mutate(body, { onSuccess: onClose });
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {policy
              ? t("planningPolicies.edit.dialogTitle")
              : t("planningPolicies.newTitle")}
          </DialogTitle>
          {policy && (
            <DialogDescription>
              {t("planningPolicies.edit.members", {
                count: policy.memberCount,
              })}{" "}
              ·{" "}
              {t("planningPolicies.edit.created", {
                distance: formatRelativeTime(policy.createdAt),
              })}
            </DialogDescription>
          )}
        </DialogHeader>
        <Form {...form}>
          <form id="policy" onSubmit={submit} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("planningPolicies.nameLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("planningPolicies.namePlaceholder")}
                      autoFocus={policy === null}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="regime"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("planningPolicies.fields.regime")}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="service_level">
                        {t("planningPolicies.regime.serviceLevel")}
                      </SelectItem>
                      <SelectItem value="min_max">
                        {t("planningPolicies.regime.minMax")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {regime === "min_max" && (
                    <FormDescription>
                      {t("planningPolicies.fields.minMaxHelp")}
                    </FormDescription>
                  )}
                </FormItem>
              )}
            />
            {regime === "service_level" && (
              <>
                <FormField
                  control={form.control}
                  name="serviceLevel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("planningPolicies.fields.serviceLevel")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step={0.01}
                          {...field}
                          onChange={(e) =>
                            field.onChange(e.target.valueAsNumber)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lotSizingMethod"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("planningPolicies.fields.lotSizingMethod")}
                      </FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="lead_time_coverage">
                            {t("planningPolicies.lotSizing.leadTimeCoverage")}
                          </SelectItem>
                          <SelectItem value="fixed_period">
                            {t("planningPolicies.lotSizing.fixedPeriod")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </>
            )}
            {regime === "service_level" &&
              lotSizingMethod === "fixed_period" && (
                <FormField
                  control={form.control}
                  name="orderCoverageDays"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("planningPolicies.fields.orderCoverageDays")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(e.target.valueAsNumber)
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        {t("planningPolicies.fields.orderCoverageHelp")}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
          </form>
        </Form>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.buttons.cancel")}
          </Button>
          <Button
            type="submit"
            form="policy"
            disabled={
              (policy !== null && !form.formState.isDirty) ||
              create.isPending ||
              update.isPending
            }
          >
            {policy ? t("common.buttons.save") : t("common.buttons.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
